/** Gallery column choices. Phones and wider screens keep separate choices. */
export type GalleryColumns = 1 | 2 | 3 | 4 | 5 | 6;
export type GalleryScreen = "mobile" | "desktop";
/** How much of a card fits: everything, a one-line caption, or the picture only. */
export type GalleryCardSize = "full" | "medium" | "tile";
export type GalleryColumnPrefs = Record<GalleryScreen, GalleryColumns>;

export const GALLERY_COLUMN_OPTIONS: Record<GalleryScreen, readonly GalleryColumns[]> = {
  mobile: [1, 2, 3, 4],
  desktop: [2, 3, 4, 5, 6],
};

export const DEFAULT_GALLERY_COLUMNS: GalleryColumnPrefs = { mobile: 1, desktop: 3 };

export const GALLERY_COLUMNS_STORAGE_KEY = "pandafilms.gallery.columns.v1";

/** Same width as Tailwind's `sm` breakpoint, where the mobile layout ends. */
export const GALLERY_DESKTOP_QUERY = "(min-width: 640px)";

export function galleryCardSize(columns: GalleryColumns, screen: GalleryScreen): GalleryCardSize {
  if (screen === "mobile") return columns >= 3 ? "tile" : columns === 2 ? "medium" : "full";
  return columns >= 6 ? "tile" : columns === 5 ? "medium" : "full";
}

/** The size slider runs from smaller items (most columns) to larger items (fewest). */
export function sizeSteps(screen: GalleryScreen): GalleryColumns[] {
  return [...GALLERY_COLUMN_OPTIONS[screen]].reverse();
}

/** Read stored choices, keeping only values offered on that screen. */
export function parseColumnPrefs(raw: string | null): GalleryColumnPrefs {
  let stored: unknown = null;
  try {
    stored = raw ? JSON.parse(raw) : null;
  } catch {
    stored = null;
  }
  const record = stored && typeof stored === "object" ? (stored as Record<string, unknown>) : {};
  const pick = (screen: GalleryScreen): GalleryColumns => {
    const value = record[screen];
    return GALLERY_COLUMN_OPTIONS[screen].find((option) => option === value) ?? DEFAULT_GALLERY_COLUMNS[screen];
  };
  return { mobile: pick("mobile"), desktop: pick("desktop") };
}
